import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withKeys as _withKeys, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "change-records-page app-page app-page--fill" }
const _hoisted_2 = { class: "page-toolbar" }
const _hoisted_3 = { class: "page-title" }
const _hoisted_4 = { class: "toolbar-actions" }
const _hoisted_5 = { class: "content-layout" }
const _hoisted_6 = { class: "list-panel" }
const _hoisted_7 = { class: "detail-panel" }
const _hoisted_8 = {
  key: 0,
  class: "empty-detail"
}
const _hoisted_9 = {
  key: 1,
  class: "detail-loading"
}
const _hoisted_10 = {
  key: 2,
  class: "detail-content"
}
const _hoisted_11 = {
  key: 0,
  class: "comparison-section"
}
const _hoisted_12 = {
  key: 0,
  class: "sub-table-summary"
}
const _hoisted_13 = {
  key: 1,
  class: "comparison-section"
}
const _hoisted_14 = {
  key: 0,
  class: "sub-table-summary"
}
const _hoisted_15 = {
  key: 3,
  class: "empty-detail"
}

import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { list } from '@/network/index'
import DynamicTable from '@/components/DynamicTable.vue'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

const SUB_TABLE_KEY = '检测原始数据详细数据子表集合'

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const EXCLUDED_BASIC_FIELDS = new Set([
  SUB_TABLE_KEY, 'ISDELETED', 'Id', 'id', '显示摘要',
  '修改前数据json', '修改后数据json', '修改前数据', '修改后数据',
  '备注图像列表json', '备注图像列表'
])
const EXCLUDED_SUB_COLUMNS = new Set(['ISDELETED', '检测原始数据详细数据主表id', 'Id', 'id'])
const pageTitle = useRouteTitle('原始数据变更记录')
const { can } = useButtonPermission('/RawDataManagement/ChangeRecords/index.vue')

const listLoading = ref(false)
const detailLoading = ref(false)
const tableData = ref<any[]>([])
const tableModel = ref<Record<string, string> | undefined>(undefined)
const searchKey = ref('')
const activeSearchKey = ref('')
const currentPage = ref(1)
const pageSize = ref(50)
const total = ref(0)
const filterParams = ref<any[]>([])
const selectedRecordId = ref<string>('')
const detailMeta = ref<Record<string, any>>({})
const beforeData = ref<Record<string, any> | null>(null)
const afterData = ref<Record<string, any> | null>(null)

const paginationConfig = reactive({
  show: true,
  get page() { return currentPage.value },
  get pageSize() { return pageSize.value },
  get total() { return total.value },
  pageSizes: [10, 20, 50, 100],
  layout: 'total, sizes, prev, pager, next, jumper',
  small: true
})

const safeParse = (value: unknown): Record<string, any> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>
  if (typeof value === 'string') {
    try { return JSON.parse(value) } catch { return null }
  }
  return null
}

const stringify = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// ---- 基本字段对比 ----
const basicFieldDiffs = computed(() => {
  const before = beforeData.value
  const after = afterData.value
  if (!before && !after) return []

  const allKeys = new Set<string>()
  if (before) Object.keys(before).forEach(k => allKeys.add(k))
  if (after) Object.keys(after).forEach(k => allKeys.add(k))

  const rows: Array<{ field: string; beforeValue: string; afterValue: string; changed: boolean }> = []
  for (const key of allKeys) {
    if (EXCLUDED_BASIC_FIELDS.has(key)) continue
    if (key.toLowerCase().includes('json')) continue
    const bv = before ? before[key] : undefined
    const av = after ? after[key] : undefined
    if (typeof bv === 'object' && bv !== null && !Array.isArray(bv)) continue
    if (typeof av === 'object' && av !== null && !Array.isArray(av)) continue
    if (Array.isArray(bv) || Array.isArray(av)) continue

    const bs = stringify(bv)
    const as_ = stringify(av)
    rows.push({ field: key, beforeValue: bs || '-', afterValue: as_ || '-', changed: bs !== as_ })
  }
  return rows
})

// ---- 子表数据 ----
const beforeSubTable = computed<Record<string, any>[]>(() => {
  const arr = beforeData.value?.[SUB_TABLE_KEY]
  return Array.isArray(arr) ? arr : []
})

const afterSubTable = computed<Record<string, any>[]>(() => {
  const arr = afterData.value?.[SUB_TABLE_KEY]
  return Array.isArray(arr) ? arr : []
})

const subTableColumns = computed(() => {
  const colSet = new Set<string>()
  const addKeys = (rows: Record<string, any>[]) => {
    rows.forEach(row => Object.keys(row).forEach(k => colSet.add(k)))
  }
  addKeys(beforeSubTable.value)
  addKeys(afterSubTable.value)

  for (const ex of EXCLUDED_SUB_COLUMNS) colSet.delete(ex)

  const hasNonEmptyValue = (col: string): boolean => {
    const check = (rows: Record<string, any>[]) => rows.some(r => {
      const v = r[col]
      return v !== undefined && v !== null && v !== '' && v !== false
    })
    return check(beforeSubTable.value) || check(afterSubTable.value)
  }

  const ordered: Array<{ prop: string; label: string; width?: number }> = []
  if (colSet.has('Header')) {
    ordered.push({ prop: 'Header', label: '行号', width: 80 })
    colSet.delete('Header')
  }
  const numbered = Array.from(colSet)
    .filter(k => /^_\d+$/.test(k))
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)))

  for (const col of numbered) {
    if (hasNonEmptyValue(col)) {
      ordered.push({ prop: col, label: col.replace('_', '列'), width: 100 })
    }
    colSet.delete(col)
  }

  for (const col of Array.from(colSet).sort()) {
    if (hasNonEmptyValue(col)) {
      ordered.push({ prop: col, label: col })
    }
  }

  return ordered
})

// ---- 子表差异映射 ----
const subTableDiffMap = computed(() => {
  const map = new Map<string, boolean>()
  const maxRows = Math.max(beforeSubTable.value.length, afterSubTable.value.length)
  for (let ri = 0; ri < maxRows; ri++) {
    const bRow = beforeSubTable.value[ri]
    const aRow = afterSubTable.value[ri]
    for (const col of subTableColumns.value) {
      const bv = bRow ? stringify(bRow[col.prop]) : ''
      const av = aRow ? stringify(aRow[col.prop]) : ''
      if (bv !== av) {
        map.set(`${ri}-${col.prop}`, true)
      }
    }
  }
  return map
})

const getBeforeCellClassName = ({ rowIndex, column }: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
  return subTableDiffMap.value.has(`${rowIndex}-${column.property}`) ? 'diff-cell-bg-before' : ''
}

const getAfterCellClassName = ({ rowIndex, column }: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
  return subTableDiffMap.value.has(`${rowIndex}-${column.property}`) ? 'diff-cell-bg-after' : ''
}

// ---- API 调用 ----
const loadList = async () => {
  try {
    listLoading.value = true
    const res = await list.rawDataChangeList(
      { page: currentPage.value, key: activeSearchKey.value, intPageSize: pageSize.value },
      filterParams.value
    )
    if (res && res.status === 200 && res.success && res.response) {
      tableData.value = Array.isArray(res.response.data) ? res.response.data : []
      total.value = res.response.dataCount || tableData.value.length
      if (res.response.model && typeof res.response.model === 'object') {
        tableModel.value = res.response.model
      }
    } else {
      ElMessage.error(res?.msg || '加载变更记录列表失败')
    }
  } catch (error) {
    console.error('加载变更记录列表失败:', error)
    ElMessage.error('加载变更记录列表失败')
  } finally {
    listLoading.value = false
  }
}

const loadDetail = async (id: string) => {
  try {
    detailLoading.value = true
    beforeData.value = null
    afterData.value = null
    detailMeta.value = {}

    const res = await list.rawDataChangeDetail({ id })
    if (res && res.status === 200 && res.success && res.response) {
      const resp = res.response
      detailMeta.value = {
        修改时间: resp.修改时间,
        提交人员: resp.提交人员,
        经营项目名称: resp.经营项目名称,
        检测项目名称: resp.检测项目名称
      }
      beforeData.value = safeParse(resp.修改前数据) ?? safeParse(resp.修改前数据json)
      afterData.value = safeParse(resp.修改后数据) ?? safeParse(resp.修改后数据json)
    } else {
      ElMessage.error(res?.msg || '加载变更详情失败')
    }
  } catch (error) {
    console.error('加载变更详情失败:', error)
    ElMessage.error('加载变更详情失败')
  } finally {
    detailLoading.value = false
  }
}

// ---- 事件处理 ----
const handleSearch = () => {
  activeSearchKey.value = searchKey.value
  currentPage.value = 1
  loadList()
}

const handleRefresh = () => loadList()

const handleFilter = (filters: any[]) => {
  filterParams.value = filters
  currentPage.value = 1
  loadList()
}

const handlePageChange = (page: number) => {
  currentPage.value = page
  loadList()
}

const handlePageSizeChange = (size: number) => {
  pageSize.value = size
  currentPage.value = 1
  loadList()
}

const handleSelectRecord = (row: any) => {
  const id = String(row.Id || row.id || '')
  if (!id) return
  selectedRecordId.value = id
  loadDetail(id)
}

onMounted(() => {
  loadList()
})

onUnmounted(() => {
  // cleanup
})

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_card = _resolveComponent("el-card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_el_card, { class: "main-card" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h2", _hoisted_3, _toDisplayString(_unref(pageTitle)), 1),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_el_input, {
              modelValue: searchKey.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchKey).value = $event)),
              placeholder: "关键字搜索",
              clearable: "",
              style: {"width":"200px"},
              onKeyup: _withKeys(handleSearch, ["enter"]),
              onClear: handleSearch
            }, null, 8, ["modelValue"]),
            _createVNode(_component_el_button, { onClick: handleSearch }, {
              default: _withCtx(() => _cache[1] || (_cache[1] = [
                _createTextVNode("搜索")
              ])),
              _: 1
            }),
            _createVNode(_component_el_button, {
              type: "primary",
              circle: "",
              size: "small",
              class: "app-refresh-btn",
              icon: _unref(Refresh),
              loading: listLoading.value,
              onClick: handleRefresh,
              title: "刷新"
            }, null, 8, ["icon", "loading"])
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(DynamicTable, {
              data: tableData.value,
              model: tableModel.value,
              loading: listLoading.value,
              "show-column-selector": false,
              "show-export": false,
              "show-detail-button": _unref(can)('详情'),
              pagination: paginationConfig,
              onDetail: handleSelectRecord,
              onFilter: handleFilter,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange
            }, null, 8, ["data", "model", "loading", "show-detail-button", "pagination"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            (!selectedRecordId.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(_component_el_empty, { description: "请在左侧列表中点击详情查看变更对比" })
                ]))
              : (detailLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(_component_el_skeleton, {
                      rows: 8,
                      animated: ""
                    })
                  ]))
                : (beforeData.value || afterData.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      (beforeSubTable.value.length > 0 || afterSubTable.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                            _cache[2] || (_cache[2] = _createElementVNode("h4", { class: "section-label section-label--before" }, "修改前数据", -1)),
                            (beforeData.value?.显示摘要)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(beforeData.value.显示摘要), 1))
                              : _createCommentVNode("", true),
                            _createVNode(_component_el_table, {
                              data: beforeSubTable.value,
                              border: "",
                              size: "small",
                              class: "data-compare-table",
                              "cell-class-name": getBeforeCellClassName
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subTableColumns.value, (col) => {
                                  return (_openBlock(), _createBlock(_component_el_table_column, {
                                    key: 'b-' + col.prop,
                                    prop: col.prop,
                                    label: col.label,
                                    width: col.width,
                                    "show-overflow-tooltip": ""
                                  }, null, 8, ["prop", "label", "width"]))
                                }), 128))
                              ]),
                              _: 1
                            }, 8, ["data"])
                          ]))
                        : _createCommentVNode("", true),
                      (beforeSubTable.value.length > 0 || afterSubTable.value.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _cache[3] || (_cache[3] = _createElementVNode("h4", { class: "section-label section-label--after" }, "修改后数据", -1)),
                            (afterData.value?.显示摘要)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(afterData.value.显示摘要), 1))
                              : _createCommentVNode("", true),
                            _createVNode(_component_el_table, {
                              data: afterSubTable.value,
                              border: "",
                              size: "small",
                              class: "data-compare-table",
                              "cell-class-name": getAfterCellClassName
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subTableColumns.value, (col) => {
                                  return (_openBlock(), _createBlock(_component_el_table_column, {
                                    key: 'a-' + col.prop,
                                    prop: col.prop,
                                    label: col.label,
                                    width: col.width,
                                    "show-overflow-tooltip": ""
                                  }, null, 8, ["prop", "label", "width"]))
                                }), 128))
                              ]),
                              _: 1
                            }, 8, ["data"])
                          ]))
                        : _createCommentVNode("", true),
                      (beforeSubTable.value.length === 0 && afterSubTable.value.length === 0)
                        ? (_openBlock(), _createBlock(_component_el_empty, {
                            key: 2,
                            description: "未检测到数据变更"
                          }))
                        : _createCommentVNode("", true)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                      _createVNode(_component_el_empty, { description: "变更详情加载失败" })
                    ]))
          ])
        ])
      ]),
      _: 1
    })
  ]))
}
}

})